/* Chat — messages, formatting, code blocks, tool results, thinking */

import { state, refs, esc, scrollChat } from './state.js';
import { api } from './api.js';
import { getAuthMode } from './auth.js';
import { wsSend } from './ws.js';
import { confirm, showTemplatePicker } from './dialogs.js';
import { highlightPython, highlightCode } from './highlight.js';
import { showPreviewPanel } from './template-preview.js';
import {
  createConfiguratorEmbed, finishConfiguratorEmbed,
  createConfiguratorCard, createConfiguratorTable,
  mountQuoteInPanel, mountConfiguratorInPanel,
} from './configurator.js';
import { loadFileTree, loadSyncStatuses } from './file-tree.js';
// agent-parts.js → agent-activity.js → agent-parts-md.js/auth.js/ws.js/
// agent-finalize-helpers.js (no chat.js anywhere in that chain — verified by
// walking each file's imports) so both of these are cycle-free.
import { renderPart } from './agent-parts.js';
import { processLabel, groupForRender, renderRollup, splitTurnItems } from './agent-activity.js';
import { serializeMessage } from './chat-persist-helpers.js';
import { liveTurnCarryover } from './chat-restore-helpers.js';

// ---------------------------------------------------------------------------
// Per-session stream containers
//
// #chatStream holds one <div class="chat-session" data-conv="…"> per session
// plus the shared welcome screen. Switching sessions shows the active container
// and hides the rest — preserving live streaming DOM for background sessions.
// ---------------------------------------------------------------------------

export function getSessionStream(convId) {
  const root = refs.chatStream || document.getElementById('chatStream');
  if (!root) return null;
  const id = convId || state.activeSessionId;
  let c = root.querySelector(`.chat-session[data-conv="${CSS.escape(id)}"]`);
  if (!c) {
    c = document.createElement('div');
    c.className = 'chat-session';
    c.dataset.conv = id;
    // Hide background sessions; the active session is visible by default.
    if (id !== state.activeSessionId) c.style.display = 'none';
    root.appendChild(c);
  }
  return c;
}

export function showSessionStream(convId) {
  const root = refs.chatStream || document.getElementById('chatStream');
  if (!root) return;
  const target = getSessionStream(convId);
  root.querySelectorAll('.chat-session').forEach((c) => {
    c.style.display = (c === target) ? '' : 'none';
  });
  // Welcome screen is shared; show it only when the active session is empty.
  const welcome = document.getElementById('chatWelcome');
  if (welcome) welcome.style.display = (target && target.childElementCount === 0) ? '' : 'none';
}

/**
 * Re-render the visible stage for the currently active session.
 * Shows that session's container, scrolls, and fires a 'session-switch' event
 * so panel modules (F5) can restore their per-session state.
 *
 * Hydration: if a reply streamed in while this session was inactive, the messages
 * were buffered in state.sessions[convId].messages but no DOM was created. If the
 * container is empty but has buffered messages, replay them into the DOM.
 */
export function renderStage(convId) {
  convId = convId || state.activeSessionId;
  showSessionStream(convId);

  // Hydrate: reconcile sess.messages vs the number of already-rendered .chat-msg
  // nodes.  This handles two cases:
  //   (a) Container empty + buffer non-empty → render all buffered messages.
  //   (b) Container partially rendered + buffer has additions (mid-stream
  //       switch-away + switch-back) → append only the missing tail.
  const container = getSessionStream(convId);
  const sess = state.sessions?.[convId];
  if (container && sess) {
    const buffered = sess.messages || [];
    const renderedCount = container.querySelectorAll('.chat-msg').length;
    if (buffered.length > renderedCount) {
      for (let i = renderedCount; i < buffered.length; i++) {
        _renderMessageToContainer(container, buffered[i]);
      }
      container.scrollTop = container.scrollHeight;
    }
  }

  scrollChat();
  window.dispatchEvent(new CustomEvent('session-switch', { detail: { convId } }));
  const input = refs.promptInput || document.getElementById('promptInput');
  if (input) input.focus();
}

/**
 * Helper: render a single message into a specific container.
 * Used by renderStage() hydration to replay buffered messages.
 */
function _renderMessageToContainer(container, msg) {
  const el = createMessageElement(msg);
  container.appendChild(el);
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export function addUserMessage(text) {
  state.messages.push({ id: `msg-${Date.now()}`, role: "user", content: text, timestamp: new Date().toISOString() });
  renderLastMessage();
  scrollChat();
}

export function addAssistantMessage(text, extra = {}) {
  state.messages.push({
    id: `msg-${Date.now()}`,
    role: "assistant",
    content: text,
    timestamp: new Date().toISOString(),
    ...extra,
  });
  renderLastMessage();
  scrollChat();
}

export function clearChat() {
  state.messages = [];
  state.activeConversationId = null;
  // Clear only the active session's container; other session containers are preserved.
  const sessStream = getSessionStream(state.activeSessionId);
  if (sessStream) sessStream.innerHTML = "";
  // Show the shared welcome screen.
  const welcome = document.getElementById('chatWelcome');
  if (welcome) {
    welcome.style.display = "";
    refs.chatWelcome = welcome;
  }
}

// opts.preserveLive — set by the HOSTED history loaders only: on a mid-run page
// reload the persisted-history fetch races the run socket's resume replay, and
// when the fetch resolves second a plain replace would clobber the streaming
// turn the replay just rebuilt (its tool cards and text live nowhere else — the
// turn persists server-side only at final). With the flag, the live trailing
// turn is carried across the rebuild and re-mounted through the live render
// pipeline, so the replayed activity stays visible and the still-arriving tail
// keeps folding into the SAME turn. Default (local loaders, rewind truncation,
// workspace restore) is untouched: replace everything, exactly as before.
export function loadConversationMessages(messages, opts) {
  const live = opts?.preserveLive ? liveTurnCarryover(state.messages, messages) : null;
  state.messages = [];
  const sessStream = getSessionStream(state.activeSessionId);
  if (!sessStream) return;
  sessStream.innerHTML = "";
  // Hide the shared welcome screen once messages are being shown.
  const welcome = document.getElementById('chatWelcome');
  if (welcome) welcome.style.display = "none";
  for (const msg of messages) {
    state.messages.push(msg);
    const el = createMessageElement(msg);
    sessStream.appendChild(el);
  }
  if (live) {
    state.messages.push(live);
    // Re-mount the live turn's bubble + activity shell and re-render its parts.
    // main.js owns that pipeline (module boundary: chat.js is imported BY
    // main.js), so reach back through a window hook — the same idiom as
    // window.__chatResendEditedMessage.
    window.__cpqRehydrateLiveTurn?.(state.activeSessionId);
  }
  scrollChat();
}

/**
 * Replay a saved messages array into the chat panel without invoking the LLM.
 * Used by the per-tenant workspace restore flow.
 */
export function restoreMessages(messages) {
  loadConversationMessages(messages || []);
}

export function getSerializableMessages() {
  // Parts (assistant richness) and the dispatched prompt (when it differs from the
  // display text) persist too, so reload/switch-back replays the full turn.
  return state.messages.map(serializeMessage);
}

function renderLastMessage() {
  // Hide (not remove) the shared welcome screen once a message exists.
  const welcome = document.getElementById('chatWelcome');
  if (welcome && state.messages.length > 0) {
    welcome.style.display = "none";
    refs.chatWelcome = welcome;
  }

  const msg = state.messages[state.messages.length - 1];
  if (!msg) return;
  // Append to this session's container, not the root #chatStream.
  const stream = getSessionStream(state.activeSessionId);
  if (!stream) return;

  const el = createMessageElement(msg);
  stream.appendChild(el);
}

function createMessageElement(msg) {
  const div = document.createElement("div");
  const isUser = msg.role === "user";
  div.className = `chat-msg ${isUser ? "chat-msg-user" : "chat-msg-asst"}`;

  if (isUser) {
    div.innerHTML = `<div class="msg-bubble">${formatContent(msg.content)}</div>`;
    _attachUserEditAffordance(div, msg);
    return div;
  } else if (!isUser && msg.parts && msg.parts.length) {
    // Replay a completed turn with the same Process/Answer layout a live turn ends in:
    // a collapsed muted Process block (reasoning + tool activity) + a primary Answer block.
    // Same bubble chrome a live turn gets (main.js ensureAssistantBubble builds
    // .chat-msg.chat-msg-asst.agent-bubble with a .msg-prose body and
    // data-role="assistant"). data-active is deliberately NOT set — that marker
    // means "streaming now" and is what ensureAssistantBubble adopts; a
    // replayed turn is already complete.
    // The legacy content-only path below is left untouched for old/legacy messages.
    div.classList.add("agent-bubble");
    div.dataset.role = "assistant";
    div.innerHTML = `<div class="msg-prose"></div>`;
    const body = div.querySelector(".msg-prose");

    // Same document-order Process/Answer split as a live turn (splitTurnItems):
    // the trail keeps narration and tools interleaved; only the text after the
    // last tool replays as the Answer.
    const items = groupForRender(msg.parts);
    const { processItems, answerItems } = splitTurnItems(items);
    const renderItem = (it) => (it.type === "rollup" ? renderRollup(it) : renderPart(it));

    // Process block — rendered only when there is reasoning/tool content (disclosure-only-
    // when-content), collapsed by default (a completed turn's resting state).
    if (processItems.length) {
      const rawMs = [msg.durationMs, msg.duration_ms, msg.duration].find(
        (v) => typeof v === "number" && Number.isFinite(v) && v >= 0);
      const proc = document.createElement("div");
      proc.className = "agent-process";
      const head = document.createElement("div");
      head.className = "process-head";
      head.setAttribute("role", "button");
      head.setAttribute("tabindex", "0");
      head.setAttribute("aria-expanded", "false");
      const icon = document.createElement("i");
      icon.className = "ic ic-sm ic-cpu";
      const label = document.createElement("span");
      label.className = "process-label";
      label.textContent = processLabel(msg.parts, rawMs != null ? rawMs : 0, true);
      const chevron = document.createElement("i");
      chevron.className = "ic ic-sm ic-chevron-down process-chevron";
      head.append(icon, label, chevron);
      const pbody = document.createElement("div");
      pbody.className = "process-body";
      for (const it of processItems) pbody.appendChild(renderItem(it));
      const toggle = () => {
        const open = proc.classList.toggle("open");
        head.setAttribute("aria-expanded", String(open));
      };
      head.addEventListener("click", toggle);
      head.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
      });
      proc.append(head, pbody);
      body.appendChild(proc);
    }

    // Answer block — primary, un-boxed prose.
    const answer = document.createElement("div");
    answer.className = "agent-answer";
    for (const it of answerItems) answer.appendChild(renderItem(it));
    body.appendChild(answer);
    return div;
  } else {
    div.innerHTML = `<div class="msg-prose">${formatContent(msg.content)}</div>`;
  }

  const body = div.querySelector(".msg-bubble, .msg-prose");

  if (msg.codeBlocks?.length) {
    for (const block of msg.codeBlocks) {
      body.appendChild(createCodeBlock(block.content, block.filename, msg.scriptName, block.codeDiff, block.lang, block.itemName, block.kind));
    }
  }

  if (msg.toolResults?.length) {
    for (const tr of msg.toolResults) {
      if (tr.code) continue;
      if (tr.content && typeof tr.content === "string") continue;
      if (tr.properties && typeof tr.properties === "object" && !tr.content) continue;
      body.appendChild(createToolResult(tr));
    }
  }

  return div;
}

// ---------------------------------------------------------------------------
// Edit-and-resend affordance (user messages)
// ---------------------------------------------------------------------------
// A small "Edit" button under each user bubble that swaps the bubble for an
// inline textarea. On resend it hands the new text to main.js — which owns the
// send path + the session messages model — via window.__chatResendEditedMessage
// (module boundary: chat.js is imported BY main.js, so it reaches back through a
// window hook, the same idiom as window.renderStage / window.loadConversationMessages).

function _attachUserEditAffordance(div, msg) {
  if (!msg || typeof msg.content !== "string") return;
  const actions = document.createElement("div");
  actions.className = "msg-actions msg-actions-user";
  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.className = "msg-edit-btn";
  editBtn.title = "Edit and resend";
  editBtn.textContent = "Edit";
  editBtn.addEventListener("click", () => _enterUserEditMode(div, msg));
  actions.appendChild(editBtn);
  div.appendChild(actions);
}

function _enterUserEditMode(div, msg) {
  const editor = document.createElement("div");
  editor.className = "msg-edit";

  const ta = document.createElement("textarea");
  ta.className = "msg-edit-input";
  ta.value = msg.content || "";
  ta.rows = Math.min(10, Math.max(2, (msg.content || "").split("\n").length));

  const actions = document.createElement("div");
  actions.className = "msg-edit-actions";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn btn-xs";
  cancelBtn.textContent = "Cancel";
  const sendBtn = document.createElement("button");
  sendBtn.type = "button";
  sendBtn.className = "btn btn-xs btn-primary";
  sendBtn.textContent = "Send";
  actions.append(cancelBtn, sendBtn);

  editor.append(ta, actions);
  div.replaceChildren(editor);
  ta.focus();
  ta.setSelectionRange(ta.value.length, ta.value.length);

  const restore = () => div.replaceWith(createMessageElement(msg));
  const submit = () => {
    const text = ta.value.trim();
    if (!text) return;
    if (typeof window.__chatResendEditedMessage === "function") {
      window.__chatResendEditedMessage(msg.id, text);
    }
  };

  cancelBtn.addEventListener("click", restore);
  sendBtn.addEventListener("click", submit);
  ta.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      restore();
    }
  });
}

export function formatContent(text) {
  if (!text) return "";
  return esc(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\n/g, "<br>");
}

// ---------------------------------------------------------------------------
// Code blocks
// ---------------------------------------------------------------------------

const IDE_ICON = `<i class="ic ic-terminal-square ico"></i>`;

function createCodeBlock(code, filename, scriptName, codeDiff, lang, itemName, kind) {
  const card = document.createElement("div");
  card.className = "code-card";

  const language = lang || "python";
  const itemKind = kind || "scripts"; // "scripts" | "calculations" | "templates" | "document"

  const header = document.createElement("div");
  header.className = "code-header";

  const headerLeft = document.createElement("div");
  headerLeft.className = "code-header-left";

  const fname = document.createElement("span");
  fname.className = "code-filename";
  fname.textContent = filename || (language === "html" ? "template.html" : language === "json" ? "properties.json" : "script.py");
  headerLeft.appendChild(fname);

  if (codeDiff) {
    const toggle = document.createElement("div");
    toggle.className = "code-view-toggle";

    const codeTab = document.createElement("button");
    codeTab.className = "code-tab active";
    codeTab.textContent = "Code";

    const diffTab = document.createElement("button");
    diffTab.className = "code-tab";
    diffTab.textContent = "Diff";

    toggle.append(codeTab, diffTab);
    headerLeft.appendChild(toggle);
  }

  const actions = document.createElement("div");
  actions.className = "code-actions";

  // --- Copy button (all languages) ---
  const copyBtn = createActionButton("Copy", async () => {
    await navigator.clipboard.writeText(code);
    copyBtn.textContent = "Copied!";
    setTimeout(() => { copyBtn.textContent = "Copy"; }, 1500);
  });
  actions.appendChild(copyBtn);

  // Hosted: chat code blocks never write files directly. /api/files/* is not on the
  // hosted allowlist (a direct write just 404s), and the product model is that changes
  // go through the agent so they stay audited and undoable. The Save affordance becomes
  // the same composer hand-off the editor uses; the agent applies the change. Never
  // auto-sends — the user reviews the prefilled request first.
  const hostedApply = getAuthMode() === "hosted";
  const makeApplyButton = (name, ext, path) => {
    const btn = createActionButton("Apply with agent", () => {
      window.dispatchEvent(new CustomEvent("agent-prefill-composer", {
        detail: { text: `Apply the ${name}.${ext} you proposed to ${path}.` },
      }));
    });
    btn.title = "Ask the agent to apply this change, so it stays audited and undoable";
    return btn;
  };

  if (language === "python") {
    // --- Python: Save, Test, Push ---
    const folderMap = { scripts: "global-scripts", calculations: "custom-calculations" };
    const folder = folderMap[itemKind] || "global-scripts";
    const kindLabel = itemKind === "calculations" ? "Calculation" : "Script";
    const pyName = scriptName || filename?.replace(/\.py$/, "") || "NewScript";

    const saveBtn = hostedApply ? makeApplyButton(pyName, "py", `${folder}/${pyName}.py`)
      : createActionButton("Save", async () => {
        const name = pyName;
        const path = `${folder}/${name}.py`;
        const ok = await confirm(`Save ${kindLabel}`, `Save to ${state.tenant}/${path}?`);
        if (ok) {
          try {
            await api.writeFile(path, code);
            addAssistantMessage(`Saved **${name}.py** to local files.`);
            await loadFileTree();
            await loadSyncStatuses();
          } catch (e) {
            addAssistantMessage(`Save failed: ${e.message}`);
          }
        }
      });

    const validateBtn = createActionButton("Validate", async () => {
      validateBtn.textContent = "Validating...";
      validateBtn.disabled = true;
      try {
        const res = await api.validateScript(code);
        const result = res?.result || res;
        if (result?.valid) {
          validateBtn.textContent = "Valid";
          validateBtn.classList.add("btn-success");
          setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.classList.remove("btn-success"); validateBtn.disabled = false; }, 2000);
        } else {
          validateBtn.textContent = "Errors";
          validateBtn.classList.add("btn-danger");
          setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.classList.remove("btn-danger"); validateBtn.disabled = false; }, 3000);
          const errors = result?.errors || [];
          if (errors.length) {
            const errBlock = card.querySelector(".code-validation-errors") || document.createElement("div");
            errBlock.className = "code-validation-errors";
            errBlock.innerHTML = errors.map(e => `<div class="code-val-error">${esc(typeof e === "string" ? e : JSON.stringify(e))}</div>`).join("");
            const body = card.querySelector(".code-body");
            if (body) body.after(errBlock);
          }
        }
      } catch (e) {
        validateBtn.textContent = "Error";
        setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.disabled = false; }, 2000);
      }
    });

    const testBtn = createActionButton("Test", () => {
      const name = scriptName || filename?.replace(/\.py$/, "") || "GeneratedScript";
      wsSend({ type: "tool_call", tool: "script_run", params: { code }, script_name: name });
      state.isThinking = true;
      renderThinking();
      scrollChat();
    });
    testBtn.classList.add("btn-primary");

    const pushBtn = createActionButton("Push", async () => {
      const name = scriptName || filename?.replace(/\.py$/, "") || "NewScript";
      const syncKind = itemKind === "calculations" ? "calculations" : "scripts";
      const ok = await confirm("Push to Cloud", `Push "${name}" (${kindLabel.toLowerCase()}) to SAP CPQ cloud?`);
      if (ok) {
        wsSend({ type: "tool_call", tool: "sync_push", params: { kind: syncKind, name } });
        state.isThinking = true;
        renderThinking();
        scrollChat();
      }
    });

    // Calculations can't run in script debugger — hide Test button
    if (itemKind === "calculations") {
      actions.append(saveBtn, validateBtn, pushBtn);
    } else {
      actions.append(saveBtn, validateBtn, testBtn, pushBtn);
    }
  } else if (language === "html") {
    // --- HTML: Save, Open in IDE, Push ---
    const name = itemName || filename?.replace(/\.html$/, "") || "Template";

    const saveBtn = hostedApply ? makeApplyButton(name, "html", `ui-templates/${name}.html`)
      : createActionButton("Save", async () => {
        const path = `ui-templates/${name}.html`;
        const ok = await confirm("Save Template", `Save to ${state.tenant}/${path}?`);
        if (ok) {
          try {
            await api.writeFile(path, code);
            addAssistantMessage(`Saved **${name}.html** to local files.`);
            await loadFileTree();
            await loadSyncStatuses();
          } catch (e) {
            addAssistantMessage(`Save failed: ${e.message}`);
          }
        }
      });

    const ideBtn = document.createElement("button");
    ideBtn.className = "btn btn-xs";
    ideBtn.innerHTML = `${IDE_ICON} Open in IDE`;
    ideBtn.addEventListener("click", async () => {
      const path = `ui-templates/${name}.html`;
      try {
        const result = await api.openInEditor(path);
        ideBtn.textContent = `Opened in ${result.editor}`;
        setTimeout(() => { ideBtn.innerHTML = `${IDE_ICON} Open in IDE`; }, 2000);
      } catch (e) {
        // File might not exist locally yet — save first, then open
        try {
          await api.writeFile(path, code);
          const result = await api.openInEditor(path);
          ideBtn.textContent = `Opened in ${result.editor}`;
          setTimeout(() => { ideBtn.innerHTML = `${IDE_ICON} Open in IDE`; }, 2000);
        } catch (e2) { alert(`Could not open editor: ${e2.message}`); }
      }
    });

    const pushBtn = createActionButton("Push", async () => {
      const ok = await confirm("Push to Cloud", `Push "${name}" to SAP CPQ cloud?`);
      if (ok) {
        wsSend({ type: "tool_call", tool: "sync_push", params: { kind: "templates", name } });
        state.isThinking = true;
        renderThinking();
        scrollChat();
      }
    });

    const previewBtn = createActionButton("Preview", async () => {
      // Save template first, then open preview panel
      const path = `ui-templates/${name}.html`;
      try {
        await api.writeFile(path, code);
      } catch (e) { /* file write might fail if not connected — preview anyway */ }
      // Also push to preview server
      try {
        await fetch('/api/template/preview/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: code, name }),
        });
      } catch (e) { /* ignore */ }
      showPreviewPanel();
    });

    // Hosted: Open in IDE targets the SERVER's desktop editor (meaningless for a browser
    // user on a hosted control plane) and Preview writeFiles + drives the desktop preview
    // server — both stay local-only.
    if (hostedApply) actions.append(saveBtn, pushBtn);
    else actions.append(saveBtn, ideBtn, previewBtn, pushBtn);
  } else if (itemKind === "document") {
    // --- Document Template: Download DOCX, Validate, Upload ---
    const name = itemName || filename?.replace(/\.docx$/, "") || "Template";

    const dlBtn = document.createElement("button");
    dlBtn.className = "btn btn-xs btn-primary";
    dlBtn.textContent = "Download DOCX";
    dlBtn.addEventListener("click", async () => {
      dlBtn.textContent = "Building...";
      dlBtn.disabled = true;
      try {
        const res = await fetch("/api/docx/build", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tag_text: code, filename: `${name}.docx` }),
        });
        if (!res.ok) throw new Error(`Build failed: ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        dlBtn.textContent = "Downloaded";
        setTimeout(() => { dlBtn.textContent = "Download DOCX"; dlBtn.disabled = false; }, 2000);
      } catch (e) {
        dlBtn.textContent = "Error";
        setTimeout(() => { dlBtn.textContent = "Download DOCX"; dlBtn.disabled = false; }, 2000);
      }
    });
    // Hosted: Download DOCX, Validate, and Upload all round-trip desktop-only routes
    // (docx build/upload, tag validate) that a hosted control plane doesn't expose;
    // only the client-side Copy stays.
    if (!hostedApply) actions.appendChild(dlBtn);

    const validateBtn = createActionButton("Validate", async () => {
      validateBtn.textContent = "Validating...";
      validateBtn.disabled = true;
      try {
        const res = await fetch("/api/tools/doc_tag_validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tag_text: code }),
        });
        const rawV = await res.json();
        const result = rawV?.result || rawV;
        if (result?.valid) {
          validateBtn.textContent = "Valid";
          validateBtn.classList.add("btn-success");
          setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.classList.remove("btn-success"); validateBtn.disabled = false; }, 2000);
        } else {
          validateBtn.textContent = "Errors";
          validateBtn.classList.add("btn-danger");
          setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.classList.remove("btn-danger"); validateBtn.disabled = false; }, 3000);
          const errors = result?.errors || [];
          if (errors.length) {
            const errBlock = card.querySelector(".code-validation-errors") || document.createElement("div");
            errBlock.className = "code-validation-errors";
            errBlock.innerHTML = errors.map(e => `<div class="code-val-error">${esc(typeof e === "string" ? e : JSON.stringify(e))}</div>`).join("");
            const body = card.querySelector(".code-body");
            if (body) body.after(errBlock);
          }
        }
      } catch (e) {
        validateBtn.textContent = "Error";
        setTimeout(() => { validateBtn.textContent = "Validate"; validateBtn.disabled = false; }, 2000);
      }
    });

    const uploadBtn = createActionButton("Upload to CPQ", async () => {
      uploadBtn.textContent = "Loading...";
      uploadBtn.disabled = true;
      try {
        const res = await fetch("/api/tools/doc_template_admin_list", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ top: 50 }),
        });
        const raw = await res.json();
        const data = raw?.result || raw;
        const templates = data?.templates || [];

        if (templates.length === 0) {
          alert("No document templates found on this tenant.");
          uploadBtn.textContent = "Upload to CPQ";
          uploadBtn.disabled = false;
          return;
        }

        const selection = await showTemplatePicker(templates);
        if (!selection) {
          uploadBtn.textContent = "Upload to CPQ";
          uploadBtn.disabled = false;
          return;
        }

        uploadBtn.textContent = "Uploading...";
        const uploadRes = await fetch("/api/docx/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tag_text: code, filename: `${name}.docx`, template_id: selection.id }),
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error(err.error || `Upload failed: ${uploadRes.status}`);
        }
        uploadBtn.textContent = "Uploaded";
        uploadBtn.classList.add("btn-success");
        setTimeout(() => { uploadBtn.textContent = "Upload to CPQ"; uploadBtn.classList.remove("btn-success"); uploadBtn.disabled = false; }, 3000);
      } catch (e) {
        uploadBtn.textContent = "Error";
        alert(`Upload failed: ${e.message}`);
        setTimeout(() => { uploadBtn.textContent = "Upload to CPQ"; uploadBtn.disabled = false; }, 2000);
      }
    });

    if (!hostedApply) actions.append(validateBtn, uploadBtn);
  } else if (language === "json") {
    // --- JSON: Open in IDE ---
    const name = itemName || filename?.replace(/\.json$/, "");
    // Derive path from filename hint (e.g. "properties/MyScript.json")
    const path = filename || `properties/${name}.json`;

    const ideBtn = document.createElement("button");
    ideBtn.className = "btn btn-xs";
    ideBtn.innerHTML = `${IDE_ICON} Open in IDE`;
    ideBtn.addEventListener("click", async () => {
      try {
        const result = await api.openInEditor(path);
        ideBtn.textContent = `Opened in ${result.editor}`;
        setTimeout(() => { ideBtn.innerHTML = `${IDE_ICON} Open in IDE`; }, 2000);
      } catch (e) {
        // Save content first then open
        try {
          await api.writeFile(path, code);
          const result = await api.openInEditor(path);
          ideBtn.textContent = `Opened in ${result.editor}`;
          setTimeout(() => { ideBtn.innerHTML = `${IDE_ICON} Open in IDE`; }, 2000);
        } catch (e2) { alert(`Could not open editor: ${e2.message}`); }
      }
    });

    // Hosted: Open in IDE writes the file to the SERVER and opens the SERVER's desktop
    // editor (meaningless for a browser user on a hosted control plane).
    if (!hostedApply) actions.appendChild(ideBtn);
  }

  header.append(headerLeft, actions);

  // Code body
  const codeBody = document.createElement("div");
  codeBody.className = "code-body";
  const pre = document.createElement("pre");
  pre.innerHTML = highlightCode(code, language);
  codeBody.appendChild(pre);

  // Diff body
  let diffBody = null;
  if (codeDiff) {
    diffBody = document.createElement("div");
    diffBody.className = "code-body code-diff-body";
    diffBody.hidden = true;
    renderCodeDiff(diffBody, codeDiff);

    const tabs = header.querySelectorAll(".code-tab");
    tabs[0].addEventListener("click", () => {
      tabs[0].classList.add("active");
      tabs[1].classList.remove("active");
      codeBody.hidden = false;
      diffBody.hidden = true;
    });
    tabs[1].addEventListener("click", () => {
      tabs[1].classList.add("active");
      tabs[0].classList.remove("active");
      codeBody.hidden = true;
      diffBody.hidden = false;
    });

    // Default to diff view for updates
    tabs[1].classList.add("active");
    tabs[0].classList.remove("active");
    codeBody.hidden = true;
    diffBody.hidden = false;
  }

  card.append(header, codeBody);
  if (diffBody) card.appendChild(diffBody);
  return card;
}

function renderCodeDiff(container, diffText) {
  // Toolbar with inline/side-by-side toggle
  const toolbar = document.createElement("div");
  toolbar.style.cssText = "display:flex;align-items:center;padding:4px 8px;border-bottom:1px solid var(--border);gap:6px;";
  const toggleDiv = document.createElement("div");
  toggleDiv.className = "cd-sbs-toggle";
  const btnInline = document.createElement("button");
  btnInline.textContent = "Inline";
  btnInline.className = "active";
  const btnSbs = document.createElement("button");
  btnSbs.textContent = "Side-by-side";
  toggleDiv.append(btnInline, btnSbs);
  toolbar.appendChild(toggleDiv);
  container.appendChild(toolbar);

  // Parse diff into structured data
  const hunks = _parseDiffHunks(diffText);

  // Inline view container
  const inlineView = document.createElement("div");
  inlineView.className = "cd-inline-view";
  _renderInlineDiff(inlineView, hunks);
  container.appendChild(inlineView);

  // Side-by-side view container (lazy-rendered)
  const sbsView = document.createElement("div");
  sbsView.className = "cd-sbs-container";
  sbsView.hidden = true;
  container.appendChild(sbsView);

  let sbsRendered = false;
  btnInline.addEventListener("click", () => {
    btnInline.classList.add("active");
    btnSbs.classList.remove("active");
    inlineView.hidden = false;
    sbsView.hidden = true;
  });
  btnSbs.addEventListener("click", () => {
    btnSbs.classList.add("active");
    btnInline.classList.remove("active");
    inlineView.hidden = true;
    sbsView.hidden = false;
    if (!sbsRendered) {
      _renderSideBySideDiff(sbsView, hunks);
      sbsRendered = true;
    }
  });
}

/** Parse unified diff text into structured hunk/line data. */
function _parseDiffHunks(diffText) {
  const lines = diffText.split("\n");
  const hunks = [];
  let current = null;

  for (const line of lines) {
    if (line.startsWith("---") || line.startsWith("+++")) continue;
    if (line.startsWith("@@")) {
      const match = line.match(/@@ -(\d+),?\d* \+(\d+),?\d* @@/);
      current = {
        header: line,
        oldStart: match ? parseInt(match[1]) : 1,
        newStart: match ? parseInt(match[2]) : 1,
        lines: [],
      };
      hunks.push(current);
    } else if (current) {
      if (line.startsWith("+")) current.lines.push({ type: "add", text: line.substring(1) });
      else if (line.startsWith("-")) current.lines.push({ type: "del", text: line.substring(1) });
      else if (line.startsWith(" ")) current.lines.push({ type: "ctx", text: line.substring(1) });
      else if (line === "") current.lines.push({ type: "ctx", text: "" });
    }
  }
  return hunks;
}

/** Word-level diff: highlight changed words within a line pair. */
function _wordHighlight(oldText, newText) {
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);

  // Simple LCS-based word diff
  const max = oldWords.length + newWords.length;
  let oldHtml = "", newHtml = "";
  let oi = 0, ni = 0;

  while (oi < oldWords.length || ni < newWords.length) {
    if (oi < oldWords.length && ni < newWords.length && oldWords[oi] === newWords[ni]) {
      oldHtml += esc(oldWords[oi]);
      newHtml += esc(newWords[ni]);
      oi++; ni++;
    } else {
      // Find next common word
      let foundOi = -1, foundNi = -1, bestDist = max;
      for (let si = oi; si < Math.min(oi + 8, oldWords.length); si++) {
        for (let sj = ni; sj < Math.min(ni + 8, newWords.length); sj++) {
          if (oldWords[si] === newWords[sj] && (si - oi + sj - ni) < bestDist) {
            foundOi = si; foundNi = sj; bestDist = si - oi + sj - ni;
          }
        }
      }
      if (foundOi >= 0) {
        for (let k = oi; k < foundOi; k++) oldHtml += `<span class="cd-word-del">${esc(oldWords[k])}</span>`;
        for (let k = ni; k < foundNi; k++) newHtml += `<span class="cd-word-add">${esc(newWords[k])}</span>`;
        oi = foundOi; ni = foundNi;
      } else {
        // No more common words — mark remaining as changed
        for (; oi < oldWords.length; oi++) oldHtml += `<span class="cd-word-del">${esc(oldWords[oi])}</span>`;
        for (; ni < newWords.length; ni++) newHtml += `<span class="cd-word-add">${esc(newWords[ni])}</span>`;
      }
    }
  }
  return { oldHtml, newHtml };
}

/** Render inline diff with word-level highlights for adjacent add/del pairs. */
function _renderInlineDiff(container, hunks) {
  const frag = document.createDocumentFragment();

  for (const hunk of hunks) {
    const hdr = document.createElement("div");
    hdr.className = "cd-hunk";
    hdr.textContent = hunk.header;
    frag.appendChild(hdr);

    let oldLine = hunk.oldStart - 1;
    let newLine = hunk.newStart - 1;
    const lines = hunk.lines;

    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const div = document.createElement("div");

      if (l.type === "add") {
        newLine++;
        // Check if previous line was a deletion (paired change) for word diff
        const prev = i > 0 ? lines[i - 1] : null;
        let content;
        if (prev && prev.type === "del") {
          const wh = _wordHighlight(prev.text, l.text);
          content = wh.newHtml;
          // Also update the previous line with word highlights (re-render)
          const prevEl = frag.lastElementChild;
          if (prevEl && prevEl.classList.contains("removal")) {
            const contentEl = prevEl.querySelector(".cd-content");
            if (contentEl) contentEl.innerHTML = wh.oldHtml;
          }
        } else {
          content = highlightPython(l.text);
        }
        div.className = "cd-line addition";
        div.innerHTML =
          `<span class="cd-num cd-num-old"></span>` +
          `<span class="cd-num cd-num-new">${newLine}</span>` +
          `<span class="cd-marker">+</span>` +
          `<span class="cd-content">${content}</span>`;
      } else if (l.type === "del") {
        oldLine++;
        div.className = "cd-line removal";
        div.innerHTML =
          `<span class="cd-num cd-num-old">${oldLine}</span>` +
          `<span class="cd-num cd-num-new"></span>` +
          `<span class="cd-marker">\u2212</span>` +
          `<span class="cd-content">${esc(l.text)}</span>`;
      } else {
        oldLine++; newLine++;
        div.className = "cd-line context";
        div.innerHTML =
          `<span class="cd-num cd-num-old">${oldLine}</span>` +
          `<span class="cd-num cd-num-new">${newLine}</span>` +
          `<span class="cd-marker"> </span>` +
          `<span class="cd-content">${esc(l.text)}</span>`;
      }
      frag.appendChild(div);
    }
  }
  container.appendChild(frag);
}

/** Render side-by-side diff view. */
function _renderSideBySideDiff(container, hunks) {
  const leftPane = document.createElement("div");
  leftPane.className = "cd-sbs-pane";
  const rightPane = document.createElement("div");
  rightPane.className = "cd-sbs-pane";

  const leftLabel = document.createElement("div");
  leftLabel.className = "cd-sbs-label";
  leftLabel.textContent = "Original";
  leftPane.appendChild(leftLabel);

  const rightLabel = document.createElement("div");
  rightLabel.className = "cd-sbs-label";
  rightLabel.textContent = "Updated";
  rightPane.appendChild(rightLabel);

  for (const hunk of hunks) {
    // Hunk headers
    for (const pane of [leftPane, rightPane]) {
      const hdr = document.createElement("div");
      hdr.className = "cd-hunk";
      hdr.textContent = hunk.header;
      pane.appendChild(hdr);
    }

    let oldLine = hunk.oldStart - 1;
    let newLine = hunk.newStart - 1;
    const lines = hunk.lines;

    // Group consecutive del+add pairs
    let i = 0;
    while (i < lines.length) {
      const l = lines[i];

      if (l.type === "ctx") {
        oldLine++; newLine++;
        const leftDiv = _makeSbsLine("context", oldLine, esc(l.text));
        const rightDiv = _makeSbsLine("context", newLine, esc(l.text));
        leftPane.appendChild(leftDiv);
        rightPane.appendChild(rightDiv);
        i++;
      } else if (l.type === "del") {
        // Collect consecutive deletions
        const dels = [];
        while (i < lines.length && lines[i].type === "del") {
          oldLine++;
          dels.push({ line: oldLine, text: lines[i].text });
          i++;
        }
        // Collect following additions
        const adds = [];
        while (i < lines.length && lines[i].type === "add") {
          newLine++;
          adds.push({ line: newLine, text: lines[i].text });
          i++;
        }
        // Pair them up
        const maxLen = Math.max(dels.length, adds.length);
        for (let j = 0; j < maxLen; j++) {
          const d = dels[j];
          const a = adds[j];
          if (d && a) {
            const wh = _wordHighlight(d.text, a.text);
            leftPane.appendChild(_makeSbsLine("removal", d.line, wh.oldHtml));
            rightPane.appendChild(_makeSbsLine("addition", a.line, wh.newHtml));
          } else if (d) {
            leftPane.appendChild(_makeSbsLine("removal", d.line, esc(d.text)));
            rightPane.appendChild(_makeSbsLine("empty", 0, ""));
          } else if (a) {
            leftPane.appendChild(_makeSbsLine("empty", 0, ""));
            rightPane.appendChild(_makeSbsLine("addition", a.line, highlightPython(a.text)));
          }
        }
      } else if (l.type === "add") {
        newLine++;
        leftPane.appendChild(_makeSbsLine("empty", 0, ""));
        rightPane.appendChild(_makeSbsLine("addition", newLine, highlightPython(l.text)));
        i++;
      } else {
        i++;
      }
    }
  }

  container.append(leftPane, rightPane);
}

function _makeSbsLine(type, lineNum, contentHtml) {
  const div = document.createElement("div");
  div.className = `cd-line ${type}`;
  const num = lineNum ? lineNum : "";
  const marker = type === "addition" ? "+" : type === "removal" ? "\u2212" : type === "empty" ? "" : " ";
  div.innerHTML =
    `<span class="cd-num">${num}</span>` +
    `<span class="cd-marker">${marker}</span>` +
    `<span class="cd-content">${contentHtml}</span>`;
  if (type === "empty") {
    div.style.opacity = "0.3";
    div.style.minHeight = "22px";
  }
  return div;
}

function createActionButton(label, onClick) {
  const btn = document.createElement("button");
  btn.className = "btn btn-xs";
  btn.textContent = label;
  btn.addEventListener("click", onClick);
  return btn;
}

// ---------------------------------------------------------------------------
// Tool results (collapsible)
// ---------------------------------------------------------------------------

function createToolResult(tr) {
  const wrapper = document.createElement("div");
  wrapper.className = "tool-result";

  const header = document.createElement("div");
  header.className = "tool-result-header";

  // Script test results
  if (tr.tool === "script_test" && tr.run_result) {
    const rr = tr.run_result;
    const ok = rr.success && !rr.error;
    header.innerHTML = `<span class="tr-label">${ok ? "\u2705" : "\u274c"} Test Result</span>` +
      (tr.source ? `<span class="tr-badge tr-badge-${tr.source}">${tr.source.replace("_", " ")}</span>` : "");

    const body = document.createElement("div");
    body.className = "tool-result-body expanded";

    if (rr.error) {
      body.innerHTML = `<div class="tr-error">${esc(String(rr.error))}</div>`;
    } else if (rr.traces?.length) {
      const lines = rr.traces.map(t =>
        `<div class="tr-trace"><span class="tr-ts">${esc(t.timestamp || "")}</span> ${esc(t.message || "")}</div>`
      ).join("");
      body.innerHTML = `<div class="tr-traces">${lines}</div>` +
        `<div class="tr-meta">${rr.trace_count} trace(s) in ${rr.duration_seconds ?? "?"}s</div>`;
    } else {
      body.innerHTML = `<div class="tr-meta">Completed (no trace output). Duration: ${rr.duration_seconds ?? "?"}s</div>`;
    }

    header.classList.add("expanded");
    wrapper.append(header, body);
    return wrapper;
  }

  // Configurator opened — embed iframe
  if (tr.action === "configurator_opened" && tr.configuration_id) {
    const configId = tr.configuration_id;
    state.cfgConfigId = configId;
    const cfgName = tr.product_name || tr.model?.product_name || "Product";
    const embed = createConfiguratorEmbed(configId, cfgName, tr.part_number || "");
    // Mount in the dedicated Configurator panel (bottom zone) so the chat
    // stream stays clean and chat/traces coexist with the product.
    if (mountConfiguratorInPanel(embed)) {
      wrapper.innerHTML = `<div class="tr-cfg-breadcrumb">⚙️ Configuring <strong>${esc(cfgName)}</strong> — opened in the Configurator panel →</div>`;
    } else {
      wrapper.replaceChildren(embed); // fallback: inline if the panel is unavailable
    }
    return wrapper;
  }

  // Quote opened — mount iframe in bottom panel, show text-only in chat
  if (tr.action === "quote_opened" && tr.quote_id) {
    // mountQuoteInPanel() is async (it may dynamic-import shell-v2.js to
    // switch views in v2 — see its own comment in configurator.js) but this
    // function (createToolResult) must stay synchronous, so the call is
    // fire-and-forget. A bare fire-and-forget on an async function is an
    // unhandled-rejection hazard if it ever throws/rejects (e.g. the dynamic
    // import failing) — log with context instead of letting it surface as an
    // uncaught "unhandledrejection".
    mountQuoteInPanel(tr.quote_id, tr.quote_number, tr.status)
      .catch((err) => console.error("[chat] mountQuoteInPanel failed:", err));
    return document.createDocumentFragment();  // No chat embed — text response handles the chat display
  }

  // Interactive configurator card
  if (tr.tool === "configure_list_attrs" && tr.model) {
    const configId = tr.configuration_id || "cfg";
    state.cfgConfigId = configId;
    const cardName = tr.model.product_name || "Product";
    const card = createConfiguratorCard(tr.model, configId, cardName, "");
    if (mountConfiguratorInPanel(card)) {
      wrapper.innerHTML = `<div class="tr-cfg-breadcrumb">⚙️ Configuring <strong>${esc(cardName)}</strong> — opened in the Configurator panel →</div>`;
    } else {
      wrapper.replaceChildren(card);
    }
    return wrapper;
  }

  // Legacy attribute table
  if (tr.tool === "configure_list_attrs" && tr.attributes && !tr.model) {
    header.innerHTML = `<span class="tr-label">\u2699\ufe0f Attributes (${tr.attribute_count || tr.attributes.length})</span>`;
    const body = document.createElement("div");
    body.className = "tool-result-body expanded";
    body.innerHTML = createConfiguratorTable(tr);
    header.classList.add("expanded");
    wrapper.append(header, body);
    return wrapper;
  }

  header.textContent = tr.tool || "Tool Result";

  const body = document.createElement("div");
  body.className = "tool-result-body";
  const pre = document.createElement("pre");
  pre.textContent = JSON.stringify(tr, null, 2);
  body.appendChild(pre);

  header.addEventListener("click", () => {
    header.classList.toggle("expanded");
    body.classList.toggle("expanded");
  });

  wrapper.append(header, body);
  return wrapper;
}

// ---------------------------------------------------------------------------
// Thinking indicator
// ---------------------------------------------------------------------------

export function renderThinking() {
  // Thinking now in-bubble; see agent-activity.js#ensureActivityShell.
  removeThinking();
}

export function removeThinking() {
  document.getElementById("thinkingIndicator")?.remove();
}
